<template lang="pug">
.add-gym-information
  StepHeader(
    :title="`Step ${step}: Add Gym Information & Location`"
    description="Provide and confirm your gym\'s information and location based on the selected gym type.")
  RogueSelect.info-field(:options="gymTypeOptions" label="Gym Type" :required="true" :modelValue="modelValue.type" @update:modelValue="updateField({type: $event})" disabledOptionText="Select Your Gym Type")
  RogueInput.info-field(v-if="modelValue.type === 'Other'" :required="true" aria-label="Your Gym Type" label="Your Gym Type" name="customGymType" :modelValue="modelValue.customType" @update:modelValue="updateField({customType: $event})" placeholder="Enter your gym type")
  RogueInput.info-field(
    v-if="modelValue.type"
    aria-label="Gym Name"
    :required="true"
    label="Gym Name"
    name="gymName"
    :modelValue="modelValue.name"
    @update:modelValue="updateField({name: $event})"
    placeholder="Enter your gym name"
    :maxlength="100"
  )
  RogueInput.info-field(v-if="modelValue.type" aria-label="Email" :required="true" label="Email" name="email" :modelValue="modelValue.email" @update:modelValue="updateField({email: $event})")
  RogueInput.info-field(v-if="phoneRequired" aria-label="Phone" :required="true" label="Phone" name="phone" :modelValue="modelValue.phone" @update:modelValue="updateField({phone: $event})")
  Checkbox.info-field#share-address-checkbox(v-if="modelValue.type && !isHomeGym" :modelValue="modelValue.shareAddress" label="Share Address" @update:modelValue="updateField({shareAddress: $event})")
  .note.info-field(v-if="modelValue.type && !isHomeGym")
    strong {{__('Note:')}}&nbsp;
    i(v-if="!modelValue.shareAddress") {{__('By unchecking, only your approximate location will appear on the map, including ZIP code, city, and country.')}}
    i(v-else) {{__('By checking \'Share Address\', your gym address and exact gym location will be displayed publicly on the map.')}}
  Field(v-if="modelValue.type && !isHomeGym" :required="true" label="Gym Address")
    AddressBook.info-field(
      :modelValue="modelValue.address"
      @update:modelValue="updateAddress($event)"
      type="gym"
    )
  Country(v-if="modelValue.type && isHomeGym" aria-label="Country" address-type="gym" :modelValue="modelValue.address?.countryId" @update:modelValue="updateCountryId($event)")
  Row(v-if="modelValue.type && isHomeGym")
    Column(width="6")
      Region(
        :required="country.requireRegion"
        :modelValue="modelValue.address?.region"
        :country-id="modelValue.address?.countryId"
        address-type="gym"
        @update:modelValue="updateRegion($event)"
      )
    Column(width="6")
      Postcode(
        :country-id="modelValue.address?.countryId"
        address-type="gym"
        :modelValue="modelValue.address?.postcode"
        @update:modelValue="updatePostcode($event)"
      )
  Checkbox.info-field#allow-drop-ins-checkbox(v-if="modelValue.type && !isHomeGym" :modelValue="modelValue.allowDropIn" @update:modelValue="updateField({allowDropIn: $event})" label="Allow Drop-Ins")
  .note.info-field(v-if="isHomeGym")
    strong {{__('Note:')}}&nbsp;
    i {{__('Only your approximate location will appear on the map, including ZIP code, city, and country.')}}
  AddGymConsent(v-if="modelValue.type" :type="modelValue.type" :agree="agreeToMapNotice" @updateConsent="$emit('agree', $event)")
</template>
<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { MARKET_SEGMENTS, type IGym } from '@rogue/universal'
import { GYM_TYPES } from './gymConstants'
import StepHeader from './StepHeader.vue'
import AddGymConsent from './AddGymConsent.vue'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useRogue } from '@/js/services/Rogue'
import type { Address } from '@/js/services/RogueTypes'

type Country = {
  requireRegion?: boolean,
}

const configStore = useConfigStore()
const customerStore = useCustomerStore()
const rogue = useRogue()

const props = defineProps<{
  modelValue: Partial<IGym>,
  agreeToMapNotice: boolean,
  step: number,
}>()

const emit = defineEmits<{(e: 'update:modelValue', val: Partial<IGym>): void, (e: 'agree', val: boolean): void}>()

const gymTypeOptions = GYM_TYPES.map(type => ({ label: type, value: type }))

const customerSegments = computed<number[]>(() => customerStore.segments || [])

const isHomeGym = computed<boolean>(() => props.modelValue.type === 'Home Gym')

const phoneRequired = computed<boolean>(() => !!props.modelValue.type && !isHomeGym.value)

const phoneValid = computed<boolean>(() => !phoneRequired.value || !!props.modelValue.phone)

const isAddressValid = computed<boolean>(() => (isHomeGym.value
  ? !!props.modelValue.address?.countryId && !!props.modelValue.address?.region && !!props.modelValue.address?.postcode
  : !!props.modelValue.address?.addressId))

const isValid = computed<boolean>(() => !!props.modelValue.type && !!props.modelValue.name && !!props.modelValue.email && phoneValid.value && isAddressValid.value && props.agreeToMapNotice && (props.modelValue.type !== 'Other' || !!props.modelValue.customType))

const country = computed<Country>(() => configStore.countries.find(c => c.alpha2 === props.modelValue.address?.countryId) || {})

async function updateField(field: Partial<IGym>) {
  const updatedGym: Partial<IGym> = Object.assign({}, props.modelValue, field)
  if (field.type === 'Home Gym' && !props.modelValue.trainingTypes?.length) {
    if (!customerSegments.value.length) {
      await customerStore.init(rogue)
      await customerStore.loadCustomer(rogue)
    }

    updatedGym.trainingTypes = Object.keys(MARKET_SEGMENTS[rogue.site.id].Interests)
      .filter(key => customerSegments.value.includes(MARKET_SEGMENTS[rogue.site.id].Interests[key]))
  }
  if (field.type === 'Home Gym' && props.modelValue.address) {
    updatedGym.address = {
      countryId : props.modelValue.address.countryId,
      region    : props.modelValue.address.region,
      postcode  : props.modelValue.address.postcode,
      city      : '',
      street    : [],
    }
  }
  emit('update:modelValue', updatedGym)
}

function updateCountryId(countryId: string) {
  const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
  if (!updatedGym.address) {
    updatedGym.address = {
      countryId : '',
      region    : '',
      postcode  : '',
      city      : '',
      street    : [],
    }
  }
  updatedGym.address!.countryId = countryId
  emit('update:modelValue', updatedGym)
}

function updateRegion(region: string) {
  const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
  if (!updatedGym.address) {
    updatedGym.address = {
      countryId : '',
      region    : '',
      postcode  : '',
      city      : '',
      street    : [],
    }
  }
  updatedGym.address!.region = region
  emit('update:modelValue', updatedGym)
}

function updatePostcode(postcode: string) {
  const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
  if (!updatedGym.address) {
    updatedGym.address = {
      countryId : '',
      region    : '',
      postcode  : '',
      city      : '',
      street    : [],
    }
  }
  updatedGym.address!.postcode = postcode
  emit('update:modelValue', updatedGym)
}

function updateAddress(address: Address) {
  const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
  updatedGym.address = {
    addressId : address._id,
    city      : address.city,
    street    : address.street,
    region    : address.region,
    postcode  : address.postcode,
    countryId : address.countryId,
  }
  emit('update:modelValue', updatedGym)
}

onMounted(() => {
  configStore.loadCountries(rogue)
})

defineExpose({
  isValid,
})
</script>
<style lang="scss" scoped>
.info-field {
  margin-bottom: $space-s;
}

.note {
  font-size: $size-xs;
}
</style>
